import React, { useState, useRef, useEffect, useCallback } from "react";

/**
 * ChatWidget — A floating AI chat box for your React app.
 *
 * Drop this component anywhere in your app (e.g., in App.js):
 *   <ChatWidget
 *     product="YOUR_PRODUCT_CODE"
 *     baseUrl="/api/chatbot"
 *     userId={currentUser?.id || ""}
 *     getAuthHeaders={getAuthHeaders}
 *   />
 *
 * Props:
 *   product      — which AI product to talk to (e.g. "YOUR_PRODUCT_CODE").
 *   apiKey       — the API key for that product
 *   adminApiKey  — admin API key for document upload (optional, enables upload button)
 *   baseUrl      — where the microservice is running (default: http://localhost:8000/api/v1)
 *   title        — the title shown in the chat header (default: "AI Assistant")
 *   context      — optional user context object { role, screen, organization_id, extra }
 *   userToken    — optional user-identity JWT (X-User-Token). Required in production
 *                  when the microservice has JWT_ENFORCE=true. Sign it on your backend
 *                  with the shared CHAT_JWT_SECRET and pass it in here.
 *   userId       — the logged-in user's id. REQUIRED for correct behavior.
 *                  • When empty/falsy, the widget renders NOTHING (so it won't
 *                    show on the login page or before auth).
 *                  • Chat history is scoped per-user, so switching users
 *                    (logout → login as someone else) gives each user their
 *                    own conversation instead of leaking the previous user's.
 *   getAuthHeaders — optional () => Headers object. When talking to your own
 *                  backend proxy (baseUrl="/api"), this merges in the app's
 *                  auth headers (e.g. Authorization: Bearer …, X-Tenant-ID)
 *                  so the proxy can identify the user. For multipart uploads
 *                  the widget drops Content-Type so the browser sets the
 *                  boundary.
 *   storageKey   — localStorage key prefix for persisting chat (default: "chatwidget")
 *
 * CHAT PERSISTENCE:
 *   The chat history and session_id are saved in localStorage, scoped per
 *   (product + userId), so they survive page navigations AND each user gets
 *   their own conversation. When the user navigates to another page and
 *   reopens the chat, their previous conversation is still there and the AI
 *   remembers it. When a different user logs in, they start fresh.
 */

const ChatWidget = ({
  product = "YOUR_PRODUCT_CODE",
  apiKey = "YOUR_PRODUCT_API_KEY",
  adminApiKey = "",
  baseUrl = "http://localhost:8000/api/v1",
  title = "AI Assistant",
  context = {},
  userToken = "",
  userId = "",
  getAuthHeaders = null,
  storageKey = "chatwidget",
}) => {
  // ── Don't render anything when there's no logged-in user ────
  // This hides the widget on the login page and before authentication.
  // (Checked AFTER all hooks below to respect React's Rules of Hooks.)

  // ── Persistent session ID (survives page navigation, per-user) ──
  // Keys are scoped by product + userId so each user gets their own chat.
  const storageId = `${storageKey}_${product}_${userId}_session_id`;
  const storageMsgs = `${storageKey}_${product}_${userId}_messages`;

  const getStoredSessionId = () => {
    try {
      let id = localStorage.getItem(storageId);
      if (!id) {
        id = `web_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        localStorage.setItem(storageId, id);
      }
      return id;
    } catch {
      return `web_${Date.now()}`;
    }
  };

  const getStoredMessages = () => {
    try {
      const stored = localStorage.getItem(storageMsgs);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const saveMessages = (msgs) => {
    try {
      localStorage.setItem(storageMsgs, JSON.stringify(msgs));
    } catch {
      // localStorage might be full or disabled — ignore
    }
  };

  // ── State ──────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(getStoredMessages);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const sessionId = useRef(getStoredSessionId());
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Persist messages to localStorage whenever they change ──
  useEffect(() => {
    saveMessages(messages);
  }, [messages]);

  // ── Reload history when the user changes (logout → login as someone else)
  // Each user has their own localStorage keys (scoped by userId), so when
  // userId changes we re-read that user's stored history + session_id.
  useEffect(() => {
    setMessages(getStoredMessages());
    sessionId.current = getStoredSessionId();
    setIsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, product]);

  // ── Auto-scroll to bottom when new messages arrive ─────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Send a message to the microservice ─────────────────────
  const sendMessage = async () => {
    const query = input.trim();
    if (!query || loading) return;

    // Add the user's message to the chat display immediately
    const userMsg = { role: "user", content: query };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      // When talking to your own backend proxy (baseUrl="/api"), merge in the
      // app's auth headers via getAuthHeaders so the proxy can identify the
      // user. When talking to the microservice directly (smoke-test mode),
      // use the X-API-Key / X-User-Token props instead.
      const authHeaders = typeof getAuthHeaders === "function" ? getAuthHeaders() : null;
      const headers = authHeaders
        ? {
            // Convert Headers / Map / object to plain object
            ...Object.fromEntries(
              authHeaders.entries ? authHeaders.entries() : Object.entries(authHeaders)
            ),
            "Content-Type": "application/json",
          }
        : {
            "X-API-Key": apiKey,
            "Content-Type": "application/json",
            // X-User-Token: required in production (JWT_ENFORCE=true).
            // Sign this JWT on your backend with the shared CHAT_JWT_SECRET
            // and pass it in via the `userToken` prop.
            ...(userToken ? { "X-User-Token": userToken } : {}),
          };

      const response = await fetch(`${baseUrl}/chat`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          product: product,
          query: query,
          session_id: sessionId.current,
          user_id: String(userId || ""),   // microservice requires a string
          context: {
            ...context,
            // Track which page the user is on
            screen: context.screen || (typeof window !== "undefined" ? window.location.pathname : "unknown"),
          },
          options: {
            response_format: "markdown",
            max_tokens: 800,
          },
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.answer },
        ]);
      } else {
        const errMsg =
          data?.error?.message || `Error ${response.status}`;
        setError(errMsg);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: `⚠️ ${errMsg}` },
        ]);
      }
    } catch (err) {
      const errMsg = "Cannot connect to the AI service. Is it running?";
      setError(errMsg);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `⚠️ ${errMsg}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // ── Upload a document ──────────────────────────────────────
  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    const allowedTypes = [".txt", ".md", ".pdf"];
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "⚠️ Only .txt, .md, and .pdf files are supported." },
      ]);
      event.target.value = "";
      return;
    }

    setUploading(true);
    setMessages((prev) => [
      ...prev,
      { role: "user", content: `📎 Uploading: ${file.name}` },
    ]);

    try {
      const formData = new FormData();
      formData.append("product", product);
      formData.append("file", file);

      // For multipart uploads, do NOT set Content-Type — the browser sets the
      // boundary automatically. When using getAuthHeaders (backend proxy mode),
      // spread the auth headers but let the browser handle the multipart header.
      const authHeaders = typeof getAuthHeaders === "function" ? getAuthHeaders() : null;
      const uploadHeaders = authHeaders
        ? Object.fromEntries(
            authHeaders.entries ? authHeaders.entries() : Object.entries(authHeaders)
          )
        : { "X-API-Key": adminApiKey };

      const response = await fetch(`${baseUrl}/admin/documents/upload`, {
        method: "POST",
        headers: uploadHeaders,
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `✅ Document uploaded successfully!\n\n**File:** ${file.name}\n**Chunks created:** ${data.chunks_created || data.chunk_count || "N/A"}\n\nYou can now ask me questions about this document and I'll answer based on its content.`,
          },
        ]);
      } else {
        const errMsg = data?.detail || `Upload failed (HTTP ${response.status})`;
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: `⚠️ Upload failed: ${errMsg}` },
        ]);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "⚠️ Cannot connect to the service for upload." },
      ]);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  // ── Clear chat history ─────────────────────────────────────
  const clearChat = () => {
    if (!window.confirm("Clear all chat history? This cannot be undone.")) return;
    setMessages([]);
    try {
      localStorage.removeItem(storageMsgs);
      // Generate a new session ID so the AI starts fresh too
      const newId = `web_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(storageId, newId);
      sessionId.current = newId;
    } catch {
      // ignore
    }
  };

  // ── Handle Enter key (send) vs Shift+Enter (newline) ───────
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Simple markdown renderer (bold, headers, lists, code) ──
  const renderMarkdown = (text) => {
    if (!text) return text;
    return text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/^### (.*$)/gm, '<div style="font-weight:600;font-size:14px;margin-top:8px">$1</div>')
      .replace(/^## (.*$)/gm, '<div style="font-weight:700;font-size:15px;margin-top:8px">$1</div>')
      .replace(/^# (.*$)/gm, '<div style="font-weight:700;font-size:16px;margin-top:8px">$1</div>')
      .replace(/^- (.*$)/gm, "<div style='margin-left:16px'>• $1</div>")
      .replace(/^\d+\. (.*$)/gm, (match, p1, offset, str) => {
        const num = str.slice(0, offset).split("\n").pop().match(/^\d+/);
        return `<div style='margin-left:16px'>${num ? num[0] : "1"}. ${p1}</div>`;
      })
      .replace(/`(.*?)`/g, '<code style="background:#f1f5f9;padding:2px 4px;border-radius:3px;font-family:monospace;font-size:13px">$1</code>');
  };

  // ── Styles (inline so no CSS file needed) ──────────────────
  const styles = {
    fab: {
      position: "fixed",
      bottom: "24px",
      right: "24px",
      width: "60px",
      height: "60px",
      borderRadius: "50%",
      backgroundColor: "#2563eb",
      color: "white",
      border: "none",
      fontSize: "26px",
      cursor: "pointer",
      boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    window: {
      position: "fixed",
      bottom: "96px",
      right: "24px",
      width: "380px",
      height: "520px",
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
      display: "flex",
      flexDirection: "column",
      zIndex: 9999,
      overflow: "hidden",
    },
    header: {
      backgroundColor: "#2563eb",
      color: "white",
      padding: "14px 18px",
      fontSize: "16px",
      fontWeight: "600",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    headerBtn: {
      background: "none",
      border: "none",
      color: "white",
      fontSize: "16px",
      cursor: "pointer",
      padding: "2px 6px",
      borderRadius: "4px",
      lineHeight: "1",
    },
    closeBtn: {
      background: "none",
      border: "none",
      color: "white",
      fontSize: "22px",
      cursor: "pointer",
      padding: "0",
      lineHeight: "1",
    },
    messages: {
      flex: 1,
      overflowY: "auto",
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      backgroundColor: "#f8fafc",
    },
    bubble: (role) => ({
      maxWidth: "85%",
      padding: "10px 14px",
      borderRadius: "12px",
      fontSize: "14px",
      lineHeight: "1.5",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      alignSelf: role === "user" ? "flex-end" : "flex-start",
      backgroundColor: role === "user" ? "#2563eb" : "#ffffff",
      color: role === "user" ? "white" : "#1e293b",
      border: role === "user" ? "none" : "1px solid #e2e8f0",
    }),
    typing: {
      alignSelf: "flex-start",
      padding: "10px 14px",
      borderRadius: "12px",
      backgroundColor: "#ffffff",
      border: "1px solid #e2e8f0",
      fontSize: "14px",
      color: "#64748b",
    },
    inputArea: {
      padding: "12px",
      borderTop: "1px solid #e2e8f0",
      display: "flex",
      gap: "8px",
      backgroundColor: "#ffffff",
      alignItems: "flex-end",
    },
    input: {
      flex: 1,
      padding: "10px 14px",
      border: "1px solid #cbd5e1",
      borderRadius: "8px",
      fontSize: "14px",
      outline: "none",
      resize: "none",
      fontFamily: "inherit",
      maxHeight: "80px",
    },
    uploadBtn: {
      padding: "10px 12px",
      backgroundColor: "#f1f5f9",
      color: "#475569",
      border: "1px solid #cbd5e1",
      borderRadius: "8px",
      fontSize: "18px",
      cursor: uploading ? "not-allowed" : "pointer",
      opacity: uploading ? 0.6 : 1,
      lineHeight: "1",
      flexShrink: "0",
    },
    sendBtn: {
      padding: "10px 18px",
      backgroundColor: "#2563eb",
      color: "white",
      border: "none",
      borderRadius: "8px",
      fontSize: "14px",
      cursor: loading ? "not-allowed" : "pointer",
      opacity: loading ? 0.6 : 1,
      flexShrink: "0",
    },
    welcome: {
      textAlign: "center",
      color: "#94a3b8",
      fontSize: "14px",
      padding: "20px",
    },
    hiddenFileInput: {
      display: "none",
    },
  };

  // ── Render ─────────────────────────────────────────────────
  // Hide entirely when there's no logged-in user (login page, pre-auth).
  // This is checked AFTER all hooks above to respect React's Rules of Hooks.
  if (!userId) return null;

  return (
    <>
      {/* Floating button — toggles the chat window */}
      <button style={styles.fab} onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? "×" : "💬"}
      </button>

      {/* Chat window — only visible when isOpen */}
      {isOpen && (
        <div style={styles.window}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerLeft}>
              <span>{title}</span>
            </div>
            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
              {/* Clear chat button */}
              {messages.length > 0 && (
                <button
                  style={styles.headerBtn}
                  onClick={clearChat}
                  title="Clear chat history"
                >
                  🗑
                </button>
              )}
              <button style={styles.closeBtn} onClick={() => setIsOpen(false)}>
                ×
              </button>
            </div>
          </div>

          {/* Messages */}
          <div style={styles.messages}>
            {messages.length === 0 && (
              <div style={styles.welcome}>
                👋 Hi! Ask me anything about {product.replace(/_/g, " ")}.
                {adminApiKey && (
                  <div style={{ marginTop: "8px", fontSize: "12px" }}>
                    📎 You can also upload documents using the paperclip button below.
                  </div>
                )}
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={idx}
                style={styles.bubble(msg.role)}
                dangerouslySetInnerHTML={
                  msg.role === "assistant"
                    ? { __html: renderMarkdown(msg.content) }
                    : undefined
                }
              >
                {msg.role === "user" ? msg.content : undefined}
              </div>
            ))}

            {loading && <div style={styles.typing}>🤔 Thinking...</div>}
            {uploading && <div style={styles.typing}>📎 Uploading document...</div>}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={styles.inputArea}>
            {/* Upload button — only shown if adminApiKey is provided */}
            {adminApiKey && (
              <>
                <button
                  style={styles.uploadBtn}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || loading}
                  title="Upload a document (.txt, .md, .pdf)"
                >
                  📎
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.md,.pdf"
                  style={styles.hiddenFileInput}
                  onChange={handleUpload}
                />
              </>
            )}

            <textarea
              style={styles.input}
              rows="1"
              placeholder="Type your question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <button
              style={styles.sendBtn}
              onClick={sendMessage}
              disabled={loading || !input.trim()}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
